import React, { useState } from 'react';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import { useAuth } from '../../../contexts/AuthContext';
import { startImpersonation } from '../../../services/admin/adminImpersonationApi';
import { getTriplicityFrontendUrl } from '../../../config/api';

export interface UserImpersonationControlsProps {
  targetUserId: string | null;
  displayName: string;
  disabled?: boolean;
}

/** "Impersonate" button + confirmation modal for a merchant or 3PL user row. */
export function UserImpersonationControls({
  targetUserId,
  displayName,
  disabled = false,
}: UserImpersonationControlsProps): React.ReactElement | null {
  const { acquireAccessToken } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);

  if (!targetUserId) {
    return null;
  }

  const handleConfirm = async (): Promise<void> => {
    const frontendUrl = getTriplicityFrontendUrl();
    if (!frontendUrl) {
      throw new Error(
        'Merchant/3PL app URL is not configured. Set REACT_APP_TRIPLICITY_FRONTEND_URL.'
      );
    }
    const result = await startImpersonation(targetUserId, acquireAccessToken);
    window.location.href = `${frontendUrl}/impersonate/accept?token=${encodeURIComponent(result.token)}`;
  };

  return (
    <>
      <button
        type="button"
        className="bo-btn bo-btn-secondary"
        onClick={() => setShowConfirm(true)}
        disabled={disabled}
        title={disabled ? 'This account is suspended and cannot be impersonated.' : undefined}
      >
        Impersonate
      </button>
      <ConfirmModal
        visible={showConfirm}
        title="Impersonate this account?"
        message={
          <p>
            You will be signed in as <strong>{displayName}</strong>. This action is logged for
            auditing. Use &quot;Exit impersonation&quot; when done.
          </p>
        }
        confirmLabel="Impersonate"
        pendingLabel="Starting…"
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
